import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useAuth } from 'contexts/AuthContext'
import { useSnackbar } from '@/hooks/common/useSnackbar'
import { doc, getDoc, updateDoc, addDoc, collection, deleteDoc, getDocs, query, where } from 'firebase/firestore'
import isEqual from 'lodash/isEqual'
import { Company } from '@otw/models'
import { db } from '@otw/models/db'

interface UseCompanyOptions {
  onSuccess?: () => void
  onError?: (error: Error) => void
  dontShowUpdates?: boolean
  enabled?: boolean
}

interface CreateCompanyParams {
  data: Omit<Company, 'id' | 'date_created' | 'date_modified' | 'created_by'>
}

interface UpdateCompanyParams {
  data: Partial<Company>
}

// Hook for managing a single company
export const useCompany = (companyId: string, options?: UseCompanyOptions) => {
    const { showSuccess, showError, showInfo, showSnackbar, showWarning } = useSnackbar()
  const queryClient = useQueryClient()
  const { userInfo, currentUser } = useAuth()

  // Only create document reference if we have a valid companyId
  const docRef = companyId ? doc(db.companies, companyId) : null

  // Define query key for this specific company
  const queryKey = ['company', companyId]

  // Define companies query key for invalidating the companies list
  const companiesQueryKey = ['companies']

  const {
    data: company,
    isLoading,
    isError,
    error,
    refetch,
  } = useQuery({
    queryKey,
    queryFn: async (): Promise<Company | null> => {
      if (!docRef) {
        return null
      }

      const snapshot = await getDoc(docRef)

      if (!snapshot.exists()) {
        return null
      }

      return snapshot.data()
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
    enabled: options?.enabled !== false && !!companyId, // Only enabled if we have a companyId
  })

  const updateCompany = useMutation({
    mutationFn: async ({ data }: UpdateCompanyParams) => {
      if (!companyId || !docRef) {
        throw new Error('Company ID is required for updates')
      }

      const snapshot = await getDoc(docRef)

      if (!snapshot.exists()) {
        throw new Error(`Company with ID ${companyId} not found`)
      }

      const currentData = snapshot.data()

      // Only update fields that have actually changed
      const actualChanges = Object.entries(data).reduce(
        (acc, [key, value]) => {
          if (!isEqual(currentData?.[key as keyof Company], value)) {
            acc[key] = value
          }
          return acc
        },
        {} as Record<string, any>,
      )

      if (Object.keys(actualChanges).length > 0) {
        // Add metadata
        const updateData = {
          ...actualChanges,
          date_modified: new Date().toISOString(),
          modified_by: userInfo?.email,
          updated_by: currentUser?.uid,
        }

        // Use regular doc reference for update (not with converter)
        const updateDocRef = doc(db.companies, companyId)

        await updateDoc(updateDocRef, updateData)
      }

      return { data: actualChanges }
    },
    onMutate: async ({ data }) => {
      if (!companyId) return { previousCompany: null }

      // Cancel any outgoing refetches for this company
      await queryClient.cancelQueries({ queryKey })

      // Snapshot the previous value
      const previousCompany = queryClient.getQueryData(queryKey)

      // Optimistically update the cache with the new value
      queryClient.setQueryData(queryKey, (old: Company | null | undefined) => {
        if (!old) return old
        return { ...old, ...data }
      })

      // Also update this company in the companies list if it exists
      const companiesData = queryClient.getQueryData<Company[]>(companiesQueryKey)

      if (companiesData) {
        queryClient.setQueryData(
          companiesQueryKey,
          companiesData.map(item => (item.id === companyId ? { ...item, ...data } : item)),
        )
      }

      return { previousCompany }
    },
    onError: (err, variables, context) => {
      // Revert to the previous value if there was an error
      if (context?.previousCompany && companyId) {
        queryClient.setQueryData(queryKey, context.previousCompany)
      }

      if (!options?.dontShowUpdates) {
        showError('Error updating company')
      }

      console.error('Error updating company:', err)
      options?.onError?.(err)
    },
    onSuccess: () => {
      if (!options?.dontShowUpdates) {
        showSuccess('Company updated successfully')
      }

      // Refetch to ensure we have the latest data
      refetch()

      options?.onSuccess?.()
    },
  })

  const deleteCompany = useMutation({
    mutationFn: async () => {
      if (!companyId) {
        throw new Error('Company ID is required for deletion')
      }

      await deleteDoc(doc(db.companies, companyId))
    },
    onSuccess: () => {
      if (!options?.dontShowUpdates) {
        showSuccess('Company deleted successfully')
      }

      // Invalidate and refetch companies list
      queryClient.invalidateQueries({ queryKey: companiesQueryKey })

      options?.onSuccess?.()
    },
    onError: err => {
      if (!options?.dontShowUpdates) {
        showError('Error deleting company')
      }

      console.error('Error deleting company:', err)
      options?.onError?.(err)
    },
  })

  const handleFieldValueChange = (field: keyof Company, value: any) => {
    if (!companyId) {
      console.warn('Cannot update field: Company ID is required')
      return
    }

    updateCompany.mutate({
      data: { [field]: value } as Partial<Company>,
    })
  }

  return {
    company,
    isLoading,
    isError,
    error,
    updateCompany: updateCompany.mutate,
    deleteCompany: deleteCompany.mutate,
    handleFieldValueChange,
    refetch,
  }
}

// Hook for managing all companies
export const useCompanies = (options?: UseCompanyOptions) => {
  const { showSuccess, showError, showInfo, showWarning } = useSnackbar()
  const queryClient = useQueryClient()
  const { userInfo, currentUser } = useAuth()

  const queryKey = ['companies']

  const {
    data: companies,
    isLoading,
    isError,
    error,
    refetch,
  } = useQuery({
    queryKey,
    queryFn: async (): Promise<Company[]> => {
      const companiesRef = db.companies
      const snapshot = await getDocs(companiesRef)

      return snapshot.docs.map(doc => doc.data())
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
    enabled: options?.enabled !== false,
  })

  const createCompany = useMutation({
    mutationFn: async ({ data }: CreateCompanyParams) => {
      const now = new Date().toISOString()

      const companyData = {
        ...data,
        date_created: now,
        date_modified: now,
        created_by: currentUser?.uid || '',
        is_active: true,
      }

      const companiesRef = db.companies
      const docRef = await addDoc(companiesRef, companyData)

      return { id: docRef.id, ...companyData }
    },
    onSuccess: newCompany => {
      if (!options?.dontShowUpdates) {
        showSuccess('Company created successfully')
      }

      // Update the companies list in cache
      queryClient.setQueryData(queryKey, (old: Company[] | undefined) => {
        return old ? [...old, newCompany] : [newCompany]
      })

      options?.onSuccess?.()
    },
    onError: err => {
      if (!options?.dontShowUpdates) {
        showError('Error creating company')
      }

      console.error('Error creating company:', err)
      options?.onError?.(err)
    },
  })

  return {
    companies: companies || [],
    isLoading,
    isError,
    error,
    createCompany: createCompany.mutate,
    refetch,
  }
}

// Hook for getting companies by user ID
export const useCompaniesByUser = (userId: string, options?: UseCompanyOptions) => {
  const queryKey = ['companies', 'byUser', userId]

  const {
    data: companies,
    isLoading,
    isError,
    error,
    refetch,
  } = useQuery({
    queryKey,
    queryFn: async (): Promise<Company[]> => {
      const companiesRef = db.companies
      const q = query(companiesRef, where('user_ids', 'array-contains', userId))
      const snapshot = await getDocs(q)

      return snapshot.docs.map(doc => doc.data())
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
    enabled: options?.enabled !== false && !!userId,
  })

  return {
    companies: companies || [],
    isLoading,
    isError,
    error,
    refetch,
  }
}
